import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, PaperPlaneTilt, CalendarBlank, CheckCircle, Buildings, IdentificationBadge, WhatsappLogo } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const C = { ink: "#20222A", sub: "#6B6E7B", line: "#E7E7EF", indigo: "#5A3FE0", indigoSoft: "#EEE9FF", green: "#0F8A5F", azure: "#2B77C4", azureSoft: "#E6F0FB" };

function SideBlock({ title, subtitle, icon: Icon, tint, recips, sel, setSel, onSend, sending, sentCount }) {
  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: C.line }} data-testid={`gov-side-${title.toLowerCase().includes("club") ? "club" : "district"}`}>
      <div className="flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: tint.soft, color: tint.fg }}><Icon size={17} weight="bold" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px] font-bold" style={{ color: C.ink }}>{title}</h3>
          <p className="text-[12px]" style={{ color: C.sub }}>{subtitle}</p>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        {recips.length === 0 && <p className="py-2 text-[13px]" style={{ color: C.sub }}>No recipients mapped.</p>}
        {recips.map((r) => (
          <label key={r.email} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-[#F6F5FD]" data-testid={`gov-recip-${r.email}`}>
            <input type="checkbox" checked={!!sel[r.email]} onChange={(e) => setSel((s) => ({ ...s, [r.email]: e.target.checked }))}
              className="h-4 w-4 shrink-0 accent-[#5A3FE0]" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13.5px] font-semibold" style={{ color: C.ink }}>{r.name}</span>
              <span className="block truncate text-[12px]" style={{ color: C.sub }}>{r.role} · {r.email}</span>
            </span>
          </label>
        ))}
      </div>
      <button onClick={onSend} disabled={sending || recips.length === 0}
        data-testid={`gov-send-${title.toLowerCase().includes("club") ? "club" : "district"}`}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[14px] font-bold text-white transition-opacity disabled:opacity-40"
        style={{ background: tint.fg }}>
        {sentCount != null ? <><CheckCircle size={16} weight="fill" /> Sent to {sentCount} · resend</> : <><PaperPlaneTilt size={15} weight="fill" /> {sending ? "Sending…" : title.includes("Club") ? "Notify club officials" : "Notify district officers"}</>}
      </button>
    </div>
  );
}

export default function InitiateGovDialog({ clubId, clubName, minDate, mode, onClose, onDone }) {
  const isResend = mode === "resend";
  const [data, setData] = useState(null);
  const [date, setDate] = useState("");
  const [sel, setSel] = useState({});
  const [sending, setSending] = useState(null);
  const [sent, setSent] = useState({});
  const [wa, setWa] = useState({});

  const isDG = (r) => {
    const x = (r.role || "").toLowerCase();
    return x.includes("district governor") && !x.includes("elect") && !x.includes("nomin") && !x.includes("assistant");
  };

  useEffect(() => {
    api.get(`/gov/clubs/${clubId}/gov-recipients`).then((r) => {
      setData(r.data);
      setDate(r.data.planned_date || "");
      const s = {};
      [...(r.data.club_side || []), ...(r.data.district_side || [])].forEach((x) => { s[x.email] = true; });
      (r.data.global_side || []).forEach((x) => { s[x.email] = isDG(x); }); // DG always in, rest opt-in
      setSel(s);
    }).catch(() => setData({ club_side: [], district_side: [], global_side: [], error: true }));
  }, [clubId]);

  const send = async (side) => {
    if (!date) { toast.error("Pick a visit date first"); return; }
    const emails = side === "club"
      ? (data.club_side || []).map((r) => r.email)
      : [...(data.district_side || []).map((r) => r.email), ...(data.global_side || []).filter((r) => isDG(r) || sel[r.email]).map((r) => r.email)];
    if (emails.length === 0) { toast.error("Select at least one recipient"); return; }
    setSending(side);
    try {
      const r = await api.post(`/gov/clubs/${clubId}/gov-notify`, { side, date, emails });
      const waMsg = r.data.whatsapp_sent != null ? ` · WhatsApp ${r.data.whatsapp_sent}/${(r.data.whatsapp || []).length}` : "";
      toast.success(`${side === "club" ? "Club" : "District"} side notified · ${r.data.notified_count} email${waMsg}`);
      setSent((p) => ({ ...p, [side]: r.data.notified_count }));
      setWa((p) => ({ ...p, [side]: r.data.whatsapp || [] }));
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    setSending(null);
  };

  const RecipRows = ({ recips, empty, lockFn }) => (
    <div className="mt-2 space-y-1.5">
      {recips.length === 0 && <p className="py-1 text-[12.5px]" style={{ color: C.sub }}>{empty}</p>}
      {recips.map((r) => {
        const locked = lockFn ? lockFn(r) : false;
        return (
          <label key={r.email} className={`flex items-center gap-2.5 rounded-lg px-2 py-1.5 ${locked ? "" : "cursor-pointer hover:bg-[#F6F5FD]"}`} data-testid={`gov-recip-${r.email}`}>
            <input type="checkbox" checked={locked ? true : !!sel[r.email]} disabled={locked}
              onChange={(e) => !locked && setSel((s) => ({ ...s, [r.email]: e.target.checked }))}
              className="h-4 w-4 shrink-0 accent-[#5A3FE0]" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13.5px] font-semibold" style={{ color: C.ink }}>{r.name}</span>
              <span className="block truncate text-[12px]" style={{ color: C.sub }}>{r.role} · {r.email}</span>
            </span>
            {locked && <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ background: C.indigoSoft, color: C.indigo }}>Always</span>}
          </label>
        );
      })}
    </div>
  );

  const GroupHead = ({ icon: Icon, tint, title, sub }) => (
    <div className="flex items-center gap-2">
      <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: tint.soft, color: tint.fg }}><Icon size={17} weight="bold" /></span>
      <div className="min-w-0 flex-1"><h3 className="text-[14px] font-bold" style={{ color: C.ink }}>{title}</h3><p className="text-[12px]" style={{ color: C.sub }}>{sub}</p></div>
    </div>
  );

  const WaFallback = ({ side }) => {
    const list = wa[side];
    if (!list || list.length === 0) return null;
    const okCount = list.filter((w) => w.sent).length;
    const failed = list.filter((w) => !w.sent);
    return (
      <div className="mt-2 rounded-xl px-3 py-2.5" style={{ background: "#F4FAF6", border: `1px solid ${C.green}22` }} data-testid={`wa-status-${side}`}>
        <p className="flex items-center gap-1.5 text-[12.5px] font-bold" style={{ color: C.green }}>
          <WhatsappLogo size={15} weight="fill" /> WhatsApp Hub · {okCount}/{list.length} sent
        </p>
        {failed.length > 0 && (
          <div className="mt-1.5 space-y-1">
            <p className="text-[11.5px]" style={{ color: C.sub }}>Send these manually:</p>
            {failed.map((w, i) => (
              w.wa_link ? (
                <a key={i} href={w.wa_link} target="_blank" rel="noreferrer" data-testid={`wa-fallback-${side}-${i}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold" style={{ background: "#fff", border: `1px solid ${C.green}55`, color: C.green }}>
                  <WhatsappLogo size={13} weight="fill" /> {w.name}
                </a>
              ) : (
                <p key={i} className="text-[12px]" style={{ color: C.sub }} data-testid={`wa-nophone-${side}-${i}`}>{w.name} — no mobile number on file</p>
              )
            ))}
          </div>
        )}
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm" data-testid="initiate-gov-dialog" onClick={onClose}>
      <div className="my-6 w-full max-w-3xl rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-[20px] font-bold tracking-tight" style={{ color: C.ink }}>{isResend ? "Resend GOV invite" : "Initiate Governor's Official Visit"}</h2>
            <p className="text-[13px] font-semibold" style={{ color: C.sub }}>{clubName || data?.club_name || "Club"}</p>
          </div>
          <button onClick={onClose} data-testid="gov-dialog-close" className="grid h-9 w-9 place-items-center rounded-full hover:bg-[#F2F2F7]" style={{ color: C.sub }}><X size={18} weight="bold" /></button>
        </div>

        <div className="mt-4 rounded-2xl p-4" style={{ background: C.indigoSoft }}>
          <label className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
            <span className="flex items-center gap-2 text-[13px] font-bold" style={{ color: C.ink }}><CalendarBlank size={16} weight="bold" /> Visit date</span>
            <input type="date" data-testid="gov-date-input" value={date} min={minDate || undefined} onChange={(e) => setDate(e.target.value)}
              className="min-h-[42px] rounded-lg border bg-white px-3 text-[14px] font-medium outline-none" style={{ borderColor: C.line, color: C.ink }} />
          </label>
          <p className="mt-2 text-[12px]" style={{ color: C.sub }}>{isResend ? "The visit is already initiated — this re-sends the invite (WhatsApp + email) to the people listed below with the current date." : "Setting the date initiates the visit. Each side then gets its own message."}</p>
        </div>

        {!data ? (
          <div className="flex justify-center py-10"><div className="h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent" style={{ color: C.indigo }} /></div>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {/* LEFT: club side + designated district side */}
            <div className="space-y-4">
              <div className="rounded-2xl border p-4" style={{ borderColor: C.line }} data-testid="gov-side-club">
                <GroupHead icon={Buildings} tint={{ soft: C.indigoSoft, fg: C.indigo }} title="Club side" sub="President & Secretary — always notified" />
                <RecipRows recips={data.club_side || []} empty="No club officers mapped." lockFn={() => true} />
                <button onClick={() => send("club")} disabled={sending} data-testid="gov-send-club"
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[14px] font-bold text-white transition-opacity disabled:opacity-40" style={{ background: C.indigo }}>
                  {sent.club != null ? <><CheckCircle size={16} weight="fill" /> Sent to {sent.club} · resend</> : <><PaperPlaneTilt size={15} weight="fill" /> {sending === "club" ? "Sending…" : "Notify club officials"}</>}
                </button>
                <WaFallback side="club" />
              </div>
              <div className="rounded-2xl border p-4" style={{ borderColor: C.line }} data-testid="gov-side-district">
                <GroupHead icon={IdentificationBadge} tint={{ soft: C.azureSoft, fg: C.azure }} title="District side (mapped)" sub="AG, Zone Governor, Zone Secretary — always notified" />
                <RecipRows recips={data.district_side || []} empty="No district officers mapped to this club." lockFn={() => true} />
              </div>
            </div>
            {/* RIGHT: optional global district officers */}
            <div className="space-y-4">
              <div className="rounded-2xl border p-4" style={{ borderColor: C.line }} data-testid="gov-side-global">
                <GroupHead icon={IdentificationBadge} tint={{ soft: "#FDECEA", fg: "#C8102E" }} title="District-wide (optional)" sub="DG, District Secretary, Treasurer — tick to include" />
                <RecipRows recips={data.global_side || []} empty="No district-wide officers." lockFn={isDG} />
              </div>
              <button onClick={() => send("district")} disabled={sending} data-testid="gov-send-district"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[14px] font-bold text-white transition-opacity disabled:opacity-40" style={{ background: C.azure }}>
                {sent.district != null ? <><CheckCircle size={16} weight="fill" /> Sent to {sent.district} · resend</> : <><PaperPlaneTilt size={15} weight="fill" /> {sending === "district" ? "Sending…" : "Notify district officers"}</>}
              </button>
              <WaFallback side="district" />
              <p className="text-[12px]" style={{ color: C.sub }}>District officers get a short note: "The GOV of {clubName || data?.club_name || "the club"} is scheduled on {(date ? date.split("-").reverse().join("-") : "dd-mm-yyyy")}."</p>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
